import { cur } from '@/lib/currency';
import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { HandCoins, AlertTriangle, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';

/**
 * OutstandingAdvancesTile — dashboard surface for the open balance of
 * pass-through advances handed to employees/partners. Goes red the
 * moment the open total crosses an env-configurable threshold so admins
 * get a built-in nudge to chase down unaccounted cash runs.
 *
 * Data source: GET /api/pass-through-advances → uses the `open_total`
 * field that the backend already computes (sum of remaining where
 * status='open'). No extra round trips.
 *
 * Threshold: react-side env var REACT_APP_ADVANCE_ALERT_THRESHOLD
 * (default 10000 SAR). Below threshold = green/healthy, above = red.
 *
 * Failure modes (graceful):
 *   • 403 (non-admin) → renders nothing
 *   • Network error → renders nothing
 *   • Empty list → renders a calm green "All accounted for" tile
 */
const fmtSR = (n) => `${cur()} ${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export function OutstandingAdvancesTile() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await api.get('/pass-through-advances');
        if (!active) return;
        setData(res.data || null);
      } catch {
        // Silent failure (RBAC / offline). Tile just doesn't render.
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  if (loading || !data) return null;

  const openTotal = Number(data.open_total) || 0;
  const openCount = (data.items || []).filter(i => i.status === 'open').length;
  const overCount = (data.items || []).filter(i => i.status === 'over_consumed').length;

  const threshold = Number(process.env.REACT_APP_ADVANCE_ALERT_THRESHOLD) || 10000;
  const breach = openTotal >= threshold;
  const tone = openCount === 0 ? 'green' : breach ? 'red' : 'amber';

  const palette = {
    green: {
      card: 'bg-emerald-50 border-emerald-200 dark:bg-emerald-950/20 dark:border-emerald-800',
      icon: 'text-emerald-600 dark:text-emerald-400',
      iconBg: 'bg-emerald-100 dark:bg-emerald-900/40',
      number: 'text-emerald-700 dark:text-emerald-300',
    },
    amber: {
      card: 'bg-amber-50 border-amber-200 dark:bg-amber-950/20 dark:border-amber-800',
      icon: 'text-amber-600 dark:text-amber-400',
      iconBg: 'bg-amber-100 dark:bg-amber-900/40',
      number: 'text-amber-700 dark:text-amber-300',
    },
    red: {
      card: 'bg-rose-50 border-rose-200 dark:bg-rose-950/20 dark:border-rose-800',
      icon: 'text-rose-600 dark:text-rose-400',
      iconBg: 'bg-rose-100 dark:bg-rose-900/40',
      number: 'text-rose-700 dark:text-rose-300',
    },
  }[tone];

  return (
    <Card
      className={`border ${palette.card} cursor-pointer hover:shadow-md transition-shadow`}
      onClick={() => navigate('/pass-through-advances')}
      data-testid="outstanding-advances-tile"
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') navigate('/pass-through-advances'); }}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg ${palette.iconBg}`}>
            <HandCoins size={18} className={palette.icon} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-xs font-bold uppercase tracking-wider text-stone-700 dark:text-stone-300">
                Outstanding Advances
              </h3>
              {breach && (
                <Badge variant="outline" className="text-[9px] uppercase tracking-wider bg-rose-100 text-rose-700 border-rose-300">
                  <AlertTriangle size={9} className="mr-0.5" /> Above threshold
                </Badge>
              )}
              {overCount > 0 && (
                <Badge variant="outline" className="text-[9px] uppercase tracking-wider bg-rose-100 text-rose-700 border-rose-300">
                  {overCount} over-consumed
                </Badge>
              )}
            </div>
            <div className="mt-1 flex items-baseline gap-2">
              <span className={`text-2xl font-bold tabular-nums ${palette.number}`} data-testid="advances-tile-amount">
                {fmtSR(openTotal)}
              </span>
              <span className="text-xs text-stone-500 dark:text-stone-400">
                {openCount === 0 ? 'all accounted for' : `across ${openCount} open advance${openCount === 1 ? '' : 's'}`}
              </span>
            </div>
            <div className="mt-1.5 inline-flex items-center gap-1 text-[10px] text-stone-500 dark:text-stone-400 hover:text-stone-700 dark:hover:text-stone-300">
              View ledger <ArrowRight size={10} />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default OutstandingAdvancesTile;
